import { useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, X } from "lucide-react";
import { formatDate } from "@/utils/formatDate";

// ── helpers ───────────────────────────────────────────────────────────────────

function getCurrentMonday(): Date {
  const today = new Date();
  const day = today.getDay() || 7;
  const mon = new Date(today);
  mon.setDate(today.getDate() - (day - 1));
  mon.setHours(0, 0, 0, 0);
  return mon;
}

function formatRange(monday: Date): string {
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return `${formatDate(monday)} – ${formatDate(sunday)}`;
}

function offsetLabel(i: number): string {
  if (i === 0) return "[0]";
  return i > 0 ? `[+${i}]` : `[${i}]`;
}

function offsetColors(i: number): { badge: string; row: string } {
  if (i < -1) return { badge: "bg-red-100 text-red-700 font-bold", row: "text-red-700" };
  if (i === -1) return { badge: "bg-red-50 text-red-500 font-semibold", row: "text-red-500" };
  if (i === 0) return { badge: "bg-orange-100 text-orange-600 font-bold", row: "text-orange-600" };
  if (i === 1) return { badge: "bg-yellow-100 text-yellow-700 font-semibold", row: "text-yellow-700" };
  if (i <= 4) return { badge: "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)] text-[var(--color-primary)]", row: "text-[var(--color-primary)]" };
  return { badge: "bg-gray-100 dark:bg-gray-700 text-gray-400", row: "text-gray-400" };
}

interface WeekOption {
  value: string;
  offset: number;
  range: string;
}

function generateOptions(pastWeeks = 8, futureWeeks = 26): WeekOption[] {
  const mon = getCurrentMonday();
  const opts: WeekOption[] = [];
  for (let i = -pastWeeks; i <= futureWeeks; i++) {
    const d = new Date(mon);
    d.setDate(mon.getDate() + i * 7);
    // Local getters instead of toISOString(): d is local midnight (setHours(0,0,0,0)),
    // toISOString() would return the previous day in UTC+2, sending wrong week to the API.
    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    opts.push({ value: iso, offset: i, range: formatRange(d) });
  }
  return opts;
}

// ── component ─────────────────────────────────────────────────────────────────

interface WeekSelectProps {
  value: string | null;
  onChange: (date: string | null) => void;
  placeholder?: string;
  className?: string;
  pastWeeks?: number;
}

export function WeekSelect({
  value,
  onChange,
  placeholder = "— brak terminu —",
  className = "",
  pastWeeks = 8,
}: WeekSelectProps) {
  const options = useMemo(() => generateOptions(pastWeeks), [pastWeeks]);
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState<{ top: number; left: number; width: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const selected = value ? options.find((o) => o.value === value) : null;

  function openDrop() {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const DROPDOWN_H = 260;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow >= DROPDOWN_H + 8 ? rect.bottom + 4 : rect.top - DROPDOWN_H - 4;
    setDropPos({ top, left: rect.left, width: Math.max(rect.width, 280) });
    setOpen(true);
  }

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // scroll to current week when opening
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (open && listRef.current) {
      const current = listRef.current.querySelector("[data-current='true']");
      current?.scrollIntoView({ block: "center" });
    }
  }, [open]);

  return (
    <div ref={containerRef} className="relative inline-block">
      <button
        ref={btnRef}
        type="button"
        onClick={() => (open ? setOpen(false) : openDrop())}
        className={`flex items-center gap-1.5 ${className}`}
      >
        {selected ? (
          <>
            <span className={`text-[11px] px-1.5 py-0.5 font-mono font-semibold ${offsetColors(selected.offset).badge}`}>
              {offsetLabel(selected.offset)}
            </span>
            <span className="text-sm">{selected.range}</span>
          </>
        ) : (
          <span className="text-gray-400 text-sm">{placeholder}</span>
        )}
        <ChevronDown size={13} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
        {selected && (
          <span
            role="button"
            onClick={(e) => { e.stopPropagation(); onChange(null); }}
            className="ml-0.5 text-gray-300 hover:text-red-400 transition-colors"
          >
            <X size={12} />
          </span>
        )}
      </button>

      {open && dropPos && createPortal(
        <div
          ref={listRef}
          style={{ position: "fixed", top: dropPos.top, left: dropPos.left, width: dropPos.width, zIndex: 9999, maxHeight: 260 }}
          className="bg-white overflow-y-auto"
        >
          <button
            type="button"
            onMouseDown={(e) => { e.preventDefault(); onChange(null); setOpen(false); }}
            className="w-full text-left px-3 py-2 text-sm text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700/50 border-b border-gray-100"
          >
            {placeholder}
          </button>
          {options.map((opt) => {
            const { badge, row } = offsetColors(opt.offset);
            const isSelected = opt.value === value;
            return (
              <button
                key={opt.value}
                type="button"
                data-current={opt.offset === 0 ? "true" : undefined}
                onMouseDown={(e) => { e.preventDefault(); onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-1.5 flex items-center gap-2 hover:bg-[var(--color-primary-dim)] transition-colors ${isSelected ? "bg-[var(--color-primary-dim)] dark:bg-[var(--color-primary-hover)]" : ""}`}
              >
                <span className={`text-[11px] px-1.5 py-0.5 font-mono font-semibold flex-shrink-0 ${badge}`}>
                  {offsetLabel(opt.offset)}
                </span>
                <span className={`text-sm ${row}`}>{opt.range}</span>
                {isSelected && <span className="ml-auto text-[var(--color-primary)] text-xs font-bold">✓</span>}
              </button>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
